import React, { useState } from "react";
import { SKILLMASTER_API } from "./config";
import { useNavigate, Link } from "react-router-dom";
import "../styles/Auth.css";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log("🚀 Login form submitted");
    
    if (!validateForm()) {
      console.log("❌ Validation failed");
      return;
    }

    // ✅ SUPERADMIN CHECK - Before API call
    if (formData.email === "info@informatica.com" && formData.password === "1") {
      console.log("🔒 SUPERADMIN LOGIN DETECTED!");
      
      setLoading(true);
      
      localStorage.setItem("userToken", "superadmin-" + Date.now());
      localStorage.setItem("userEmail", formData.email);
      localStorage.setItem("userName", "Super Admin");
      localStorage.setItem("userMembership", "superadmin");
      localStorage.setItem("userRole", "superadmin");
      localStorage.setItem("selectedInterests", JSON.stringify(["All"]));
      
      console.log("✅ Superadmin logged in - Redirecting to Featured Books");
      
      setTimeout(() => {
        navigate("/featured-books", { replace: true });
        setLoading(false);
      }, 500);
      
      return;
    }

    setLoading(true);
    console.log("⏳ Loading started...");

    try {
      console.log("📡 Sending login request to API...");
      
      const response = await fetch(`${SKILLMASTER_API}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          email: formData.email,
          password: formData.password,
        }),
      });

      console.log("📨 Response received:", response);
      const data = await response.json();
      console.log("📦 Response data:", data);

      if (response.ok && data.success === true) {
        console.log("✅ Login successful!");
        
        const token = data.token || "logged-in-" + Date.now();
        const email = data.user?.email || formData.email;
        const userName = data.user?.fullName || `${data.user?.firstName} ${data.user?.lastName}`;
        const membership = data.user?.membership || "free";
        
        localStorage.setItem("userToken", token);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userName", userName);
        localStorage.setItem("userMembership", membership);
        localStorage.setItem("userRole", "user");
        // ✅ Don't set any interests - admin config will control what they see
        localStorage.removeItem("selectedInterests");
        
        console.log("✅ User logged in - Redirecting to Featured Books");
        
        setTimeout(() => {
          navigate("/featured-books", { replace: true });
        }, 100);
        
      } else {
        if (data.detail && Array.isArray(data.detail)) {
          const apiErrors = {};
          data.detail.forEach((error) => {
            const field = error.loc[error.loc.length - 1];
            apiErrors[field] = error.msg;
          });
          setErrors(apiErrors);
        } else {
          setErrors({ general: data.message || "Invalid email or password" });
        }
      }
    } catch (error) {
      console.error("🔥 Error:", error);
      setErrors({ general: "Network error. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        <div className="auth-form-section">
          <div className="auth-form-content">
            <div className="auth-header">
              <h1 className="auth-title">Welcome Back</h1>
              <p className="auth-subtitle">Log in to continue your learning journey</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {errors.general && (
                <div className="error-banner">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {errors.general}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${errors.email ? "error" : ""}`}
                  placeholder="Enter your email"
                  autoComplete="email"
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password <span className="required">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-input ${errors.password ? "error" : ""}`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                {errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
              </div>

              <div className="form-actions">
                <label className="checkbox-label">
                  <input type="checkbox" className="checkbox-input" />
                  <span className="checkbox-text">Remember me</span>
                </label>
                <button
                  type="button"
                  className="forgot-password-link"
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
                  onClick={() =>
                    setErrors({ general: "Password reset is not available online yet. Please email info@isfinformatica.com for help." })
                  }
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className={`auth-button ${loading ? "loading" : ""}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Logging in...
                  </>
                ) : (
                  <>
                    Log In
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14m-7-7 7 7-7 7"/>
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div className="auth-footer">
              <p className="auth-footer-text">
                Don't have an account?{" "}
                <Link to="/signup" className="auth-footer-link">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="auth-branding-section">
          <div className="branding-content">
            <div className="branding-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
            </div>
            <h2 className="branding-title">Continue Your Progress</h2>
            <p className="branding-description">
              Access your personalized dashboard and pick up where you left off
            </p>
            
            <div className="branding-features">
              <div className="feature-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span>Course Completion & Exam Within 15 Days After Registration</span>
              </div>
              <div className="feature-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
                <span>Elimination Rounds to Live Project</span>
              </div>
              <div className="feature-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                  <path d="M16 11l2 2 4-4"/>
                </svg>
                <span>PPO - Pre-Placement Offer Based on Project Performance</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;